import type { AnalyticsProperties } from './events';
import { currentPageContext } from './pageContext';

import type { Brand } from '@/shared/types/brand';
import type { User } from '@/shared/types/user';

/**
 * PostHog renders a missing property as "None" in a breakdown, which is
 * indistinguishable from a value we failed to attach. Explicit sentinels keep
 * three genuinely different situations apart:
 *
 *   'unknown'  — /auth/me has not resolved yet, so we do not know.
 *   'guest'    — resolved, no session. Public-page traffic.
 *   'none'     — authenticated, but this dimension is empty (no org on the
 *                user, no brand selected, page outside the feature nav).
 */
export const AUTH_UNKNOWN = 'unknown';
export const AUTH_GUEST = 'guest';
export const AUTH_AUTHENTICATED = 'authenticated';
export const NONE = 'none';

export type AuthState =
  | typeof AUTH_UNKNOWN
  | typeof AUTH_GUEST
  | typeof AUTH_AUTHENTICATED;

interface IdentitySnapshot {
  authState: AuthState;
  role: string | null;
  org: { id: string; name: string; status: string } | null;
  brand: { id: string; name: string; status: string } | null;
}

const UNRESOLVED: IdentitySnapshot = {
  authState: AUTH_UNKNOWN,
  role: null,
  org: null,
  brand: null
};

let snapshot: IdentitySnapshot = UNRESOLVED;

export function setAuthContext(user: User | null): void {
  if (!user) {
    // Clearing the user clears the brand too: a brand is only meaningful
    // relative to a session, and useBrandStore is not reset on logout.
    snapshot = {
      authState: AUTH_GUEST,
      role: null,
      org: null,
      brand: null
    };
    return;
  }

  snapshot = {
    authState: AUTH_AUTHENTICATED,
    role: user.role ?? null,
    org: user.organization
      ? {
          id: user.organization.id,
          name: user.organization.name,
          status: user.organization.status
        }
      : null,
    brand: snapshot.brand
  };
}

export function setBrandContext(
  brand: Pick<Brand, 'id' | 'name' | 'status'> | null
): void {
  snapshot = {
    ...snapshot,
    brand: brand
      ? { id: brand.id, name: brand.name, status: brand.status }
      : null
  };
}

/** Test hook, and the reset path if the SDK is ever torn down. */
export function resetAnalyticsContext(): void {
  snapshot = UNRESOLVED;
}

// An absent value means "guest"/"unknown" when there is no session, and "none"
// when there is one — that distinction is the whole point of the sentinels.
const orSentinel = (
  value: string | null | undefined,
  state: AuthState
): string => value ?? (state === AUTH_AUTHENTICATED ? NONE : state);

export function getAnalyticsContext(): AnalyticsProperties {
  const { authState, role, org, brand } = snapshot;

  return {
    auth_state: authState,
    is_authenticated: authState === AUTH_AUTHENTICATED,
    user_role: orSentinel(role, authState),
    org_id: orSentinel(org?.id, authState),
    org_name: orSentinel(org?.name, authState),
    org_status: orSentinel(org?.status, authState),
    brand_id: orSentinel(brand?.id, authState),
    brand_name: orSentinel(brand?.name, authState),
    brand_status: orSentinel(brand?.status, authState),
    ...currentPageContext()
  };
}
